import { onAuthStateChanged, type User } from "firebase/auth";
import { LogOut } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { completeRedirectSignIn, configureFirebase, getFirebaseAuth, signInWithGoogle, signOutUser } from "../lib/firebaseClient";
import type { RuntimeConfig } from "../lib/api";

interface AuthGateProps {
  config: RuntimeConfig;
  onAuthChange: () => void;
  children: React.ReactNode;
}

export function AuthGate({ config, onAuthChange, children }: AuthGateProps) {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const firebaseConfigured = useMemo(() => Boolean(configureFirebase(config.firebase)), [config]);

  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) {
      setAuthReady(true);
      return undefined;
    }
    void completeRedirectSignIn().catch(() => undefined);
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setAuthReady(true);
      if (nextUser) onAuthChange();
    });
  }, [firebaseConfigured, onAuthChange]);

  if (!firebaseConfigured) {
    return (
      <div className="auth-banner">
        <span>Demo mode</span>
        <p>Firebase is not configured yet. The app is using local demo records.</p>
        {children}
      </div>
    );
  }

  if (!authReady) return <div className="screen-center">Loading auth...</div>;

  if (!user) {
    return (
      <main className="login-screen">
        <section className="login-panel">
          <div className="brand-mark">P</div>
          <h1>Posterly</h1>
          <p>Sign in with an allowlisted Google account to log placements and view poster performance.</p>
          <button className="primary-button" onClick={() => void signInWithGoogle()}>
            Sign in with Google
          </button>
        </section>
      </main>
    );
  }

  return (
    <>
      <div className="user-strip">
        <span>{user.email}</span>
        <button className="icon-text-button" onClick={() => void signOutUser()}>
          <LogOut size={16} /> Sign out
        </button>
      </div>
      {children}
    </>
  );
}
